import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";

import { InfoApi } from "../../api/InfoApi";
import { OrgAdminApi } from "../../api/OrgAdminApi";
import {
  PREVIEW_SUBTITLE,
  parseLeaderboardCode,
  previewColumnHeader,
  previewMetric,
  previewMetricHeader,
  previewRowText,
  previewTitle,
} from "../../helper/leaderboardPreview";
import { getlocalStorageUserObject } from "../../helper/LocalStorageHelper";

function asList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
}

function currentOrgId() {
  try {
    const user = getlocalStorageUserObject();
    return user?.organization_id ?? "";
  } catch {
    return "";
  }
}

function errorText(msg) {
  if (typeof msg === "string" && msg) {
    return msg;
  }
  if (typeof msg?.data?.message === "string" && msg.data.message) {
    return msg.data.message;
  }
  return "讀取排行榜失敗";
}

export default function LeaderboardPreviewDialog({ open, code, onClose }) {
  const requestKey = open ? code || "" : "";
  const [seenKey, setSeenKey] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [skills, setSkills] = useState([]);
  const orgId = currentOrgId();
  const parsed = parseLeaderboardCode(code);
  const skill = skills.find((item) => item.code === parsed?.skillCode);
  const skillName = skill?.name_zh || skill?.name_en || "";
  const title = previewTitle(code, skillName);

  if (requestKey !== seenKey) {
    setSeenKey(requestKey);
    if (requestKey) {
      setLoading(true);
      setRows([]);
      setError("");
    }
  }

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let active = true;
    InfoApi.getSkillList()
      .then((data) => {
        if (active) {
          setSkills(asList(data));
        }
      })
      .catch(() => {
        if (active) {
          setSkills([]);
        }
      });

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !code) {
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError("");
    setRows([]);
    OrgAdminApi.getLeaderboardPreviewCallback(code, (isSuccess, msg, data) => {
      if (!active) {
        return;
      }
      setLoading(false);
      if (!isSuccess) {
        setRows([]);
        setError(errorText(msg));
        return;
      }
      setRows(asList(data));
    });

    return () => {
      active = false;
    };
  }, [open, code]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      aria-labelledby="leaderboard-preview-title"
      PaperProps={{
        sx: {
          bgcolor: "#f4f5f7",
          backgroundImage: "none",
          borderRadius: 3,
        },
      }}
    >
      <IconButton
        aria-label="關閉"
        onClick={onClose}
        sx={{ position: "absolute", top: 12, right: 12, color: "#6b7280" }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ px: { xs: 2.5, md: 4 }, pt: 4, pb: 3 }}>
        <Typography
          id="leaderboard-preview-title"
          sx={{ pr: 5, fontSize: 22, fontWeight: 800, lineHeight: 1.3, color: "#111827" }}
        >
          {title || "排行榜"}
        </Typography>
        <Typography sx={{ mt: 0.5, mb: 2.5, fontSize: 13, color: "#9ca3af" }}>
          {PREVIEW_SUBTITLE}
        </Typography>

        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: "16px",
            minHeight: 420,
            px: { xs: 2, md: 3 },
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "72px minmax(0, 1fr) auto",
              columnGap: 2,
              alignItems: "center",
              px: 1,
              py: 1.5,
              borderBottom: "1px solid #eef0f3",
              color: "#9ca3af",
              fontSize: 13,
            }}
          >
            <Box>名次</Box>
            <Box>{previewColumnHeader(code)}</Box>
            <Box sx={{ textAlign: "right" }}>{previewMetricHeader(code)}</Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", minHeight: 280 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {!loading && error ? (
            <Typography sx={{ px: 1, py: 3, color: "#b91c1c" }}>{error}</Typography>
          ) : null}

          {!loading && !error && rows.length === 0 ? (
            <Typography sx={{ px: 1, py: 3, color: "#6b7280" }}>未有排名資料</Typography>
          ) : null}

          {!loading && !error
            ? rows.map((row, index) => {
                const text = previewRowText(code, row, orgId);
                const metric = previewMetric(code, row, skill?.unit);
                const champion = Number(row?.rank) === 1;
                return (
                  <Box
                    key={`${row?.rank ?? index}-${index}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "72px minmax(0, 1fr) auto",
                      columnGap: 2,
                      alignItems: "center",
                      px: 1,
                      py: 2,
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <Box sx={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>{row?.rank}</Box>
                    <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Box sx={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>{text.name}</Box>
                      {champion ? (
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.15,
                            borderRadius: 999,
                            bgcolor: "#F6E7A8",
                            color: "#7A5B10",
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: "20px",
                          }}
                        >
                          榜首
                        </Box>
                      ) : null}
                      {text.meta ? (
                        <Box sx={{ fontSize: 14, color: "#9ca3af" }}>{text.meta}</Box>
                      ) : null}
                    </Box>
                    <Box sx={{ fontSize: 16, fontWeight: 800, color: "#111827", whiteSpace: "nowrap" }}>
                      {metric.value}
                      {metric.unit ? (
                        <Box component="span" sx={{ ml: 0.75, fontWeight: 700 }}>
                          {metric.unit}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                );
              })
            : null}
        </Box>
      </Box>
    </Dialog>
  );
}
